import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  SquaresFour, Envelope, CalendarBlank, Users, FileText,
  Gear, Bell, SignOut, Sparkle, Sun, Moon,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

const nav = [
  { to: "/dashboard", label: "Dashboard", Icon: SquaresFour, testid: "nav-dashboard" },
  { to: "/inbox", label: "Inbox", Icon: Envelope, testid: "nav-inbox" },
  { to: "/calendar", label: "Calendar", Icon: CalendarBlank, testid: "nav-calendar" },
  { to: "/customers", label: "Customers", Icon: Users, testid: "nav-customers" },
  { to: "/quotes", label: "Quotes", Icon: FileText, testid: "nav-quotes" },
  { to: "/notifications", label: "Notifications", Icon: Bell, testid: "nav-notifications" },
  { to: "/settings", label: "Settings", Icon: Gear, testid: "nav-settings" },
];

export default function AppShell() {
  const { user, logout, loading } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFDFD] dark:bg-neutral-950">
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] dark:bg-neutral-950 text-neutral-950 dark:text-neutral-50 flex" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <aside className="w-64 border-r border-neutral-200 dark:border-neutral-800 min-h-screen sticky top-0 flex flex-col bg-white dark:bg-neutral-900">
        <div className="px-6 pt-6 pb-8 flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[#0F52BA] flex items-center justify-center">
            <Sparkle weight="fill" className="text-white" size={18} />
          </div>
          <div className="font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>AI Office</div>
        </div>
        <nav className="px-3 flex-1 flex flex-col gap-1">
          {nav.map(({ to, label, Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-neutral-950 text-white dark:bg-neutral-100 dark:text-neutral-950" : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                }`
              }
            >
              <Icon size={18} weight="duotone" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-3">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-neutral-200 dark:bg-neutral-800" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user.name || user.email}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500">{user.role}</div>
            </div>
            <button
              data-testid="theme-toggle"
              onClick={toggle}
              className="p-2 rounded-md text-neutral-500 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
              title={theme === "dark" ? "Switch to light" : "Switch to dark"}
            >
              {theme === "dark" ? <Sun size={16} weight="fill" /> : <Moon size={16} weight="fill" />}
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="p-2 rounded-md text-neutral-500 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:text-neutral-100 transition-colors"
              title="Sign out"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
